import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  ChevronDown,
  LayoutList,
  LayoutTemplate,
  List,
  Mail,
  MessageCircle,
  Palette,
  Plus,
  Settings,
  Star,
  Store,
  Tag,
} from 'lucide-react';
import { api } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { useCurrentBot } from '@/lib/current-bot';
import type { PartnerBot } from '@/api/types';
import { BotAvatar, BotPicker } from '@/components/BotPicker';
import { RowGroup, RowLink } from '@/components/ui/row-link';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';

/**
 * Вкладка «Бот»: выбранный бот, три его цифры и разделы настроек, как раздел «Бот»
 * в кабинете на сайте. До 18.10.2026 половина строк открывала одну карточку на 25
 * раскрывашек (`/bots/<id>`); теперь у каждой строки свой экран.
 */
export const Route = createFileRoute('/bot/')({ component: BotHub });

function BotHub() {
  const navigate = useNavigate();
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: bots = [], isPending, isError, refetch } = useQuery({
    queryKey: ['bots'],
    queryFn: () => api.bots(),
    staleTime: 60_000,
  });
  const bot = useCurrentBot(bots);

  if (isPending) return <BotHubSkeleton />;

  if (isError) {
    return (
      <div className="p-4 pt-10 text-center space-y-3">
        <p className="text-sm text-muted-foreground">Не удалось загрузить ботов.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="glass glass-flat rounded-full px-5 py-2.5 text-sm font-semibold"
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="p-4 pt-12 flex flex-col items-center text-center gap-3">
        <h1 className="font-display text-[22px] font-medium">Ботов пока нет</h1>
        <p className="max-w-[30ch] text-sm text-muted-foreground">
          Создайте бота, и здесь появятся его тарифы, тексты, оформление и витрина.
        </p>
        <Link
          to="/onboarding/bot"
          onClick={() => haptic('light')}
          className="btn-glass-red mt-2 inline-flex h-11 items-center gap-2 rounded-[12px] px-5 text-[15px] font-semibold"
        >
          <Plus className="size-[18px]" /> Создать бота
        </Link>
      </div>
    );
  }

  const go = (to: string, params?: Record<string, string>, search?: Record<string, string>) => {
    haptic('light');
    void navigate({ to, params: params as never, search: search as never });
  };
  const siteHost = bot.siteUrl ? bot.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : null;

  return (
    <div className="p-4 pb-6 space-y-5">
      {/* Шапка: какой бот сейчас выбран */}
      <div className="flex flex-col items-center pt-3 text-center">
        <BotAvatar bot={bot} size={64} />
        <h1 className="mt-3 max-w-full truncate font-display text-[21px] font-medium">{bot.brandName || bot.username}</h1>
        <div className="mt-1 flex max-w-full items-center gap-1.5 text-[13px] text-dim">
          <span className="truncate">@{bot.username}</span>
          <span aria-hidden="true">·</span>
          <span className={`size-1.5 shrink-0 rounded-full ${bot.status === 'active' ? 'bg-money' : 'bg-white/30'}`} />
          <span className="shrink-0">{bot.status === 'active' ? 'работает' : 'на паузе'}</span>
        </div>
        {bots.length > 1 && (
          <button
            type="button"
            onClick={() => {
              haptic('light');
              setPicking(true);
            }}
            className="glass glass-flat mt-3 inline-flex items-center gap-1 rounded-full py-1.5 pl-3 pr-2.5 text-[13px] text-muted-foreground"
          >
            Другой бот <ChevronDown className="size-4" />
          </button>
        )}
      </div>

      <BotNumbers bot={bot} />

      <Group title="Продажи">
        <RowLink
          icon={<Tag />}
          label="Тарифы и оплата"
          hint="Цены, наценка, способы оплаты"
          onClick={() => go('/pricing', undefined, { botId: bot.id })}
        />
        <RowLink
          icon={<Store />}
          label="Витрина"
          hint={siteHost ?? 'Сайт, где покупают без Telegram'}
          trailing={
            <span className="shrink-0 text-[13px] text-dim">{bot.siteEnabled ? 'работает' : 'выключена'}</span>
          }
          chevron={false}
          onClick={() => openSiteSettings(bot.id)}
        />
      </Group>

      <Group title="Как выглядит бот">
        <RowLink
          icon={<LayoutTemplate />}
          label="Шаблоны оформления"
          hint="Готовые картинки, иконки и темы одним нажатием"
          onClick={() => go('/bot/templates', undefined, { bot: bot.id })}
        />
        <RowLink
          icon={<LayoutList />}
          label="Меню и тексты"
          hint="Кнопки, приветствие, экраны"
          onClick={() => go('/bot-texts/$botId', { botId: bot.id })}
        />
        <RowLink
          icon={<Palette />}
          label="Оформление"
          hint="Имя, цвет, картинки, подписка в приложении"
          onClick={() => go('/bot/brand', undefined, { bot: bot.id })}
        />
        <RowLink
          icon={<MessageCircle />}
          label="Автосообщения"
          hint="Пробник, продление, возврат"
          onClick={() => go('/bot-warmup/$botId', { botId: bot.id })}
        />
        <RowLink
          icon={<Mail />}
          label="Свои автосообщения"
          hint="Свой сценарий и срок"
          onClick={() => go('/warmup-rules/$botId', { botId: bot.id })}
        />
      </Group>

      <Group title="Клиенты бота">
        <RowLink
          icon={<Star />}
          label="Отзывы"
          hint="Ответы, свои отзывы, награда"
          onClick={() => go('/bot-reviews/$botId', { botId: bot.id })}
        />
      </Group>

      <Group title="Бот">
        <RowLink
          icon={<Settings />}
          label="Настройки бота"
          hint="Поддержка, каналы, логи, токен"
          onClick={() => go('/bot/settings', undefined, { bot: bot.id })}
        />
        <RowLink
          icon={<List />}
          label={bots.length > 1 ? `Все боты: ${bots.length}` : 'Все боты'}
          hint="Добавить ещё одного"
          to="/bots"
        />
      </Group>

      <BotPicker open={picking} onClose={() => setPicking(false)} bots={bots} current={bot.id} />
    </div>
  );
}

/** Витрину настраивают на сайте: логотип, темы и редактор вопросов это работа за
 * компьютером, второй такой же редактор в мини-аппе через месяц разошёлся бы с первым. */
function openSiteSettings(botId: string) {
  const url = `https://hexpartners.pro/vpn/app/site?bot=${botId}`;
  const tg = getWebApp();
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, '_blank', 'noopener');
}

/** Три цифры бота прямо под шапкой. Из списка ботов, отдельного запроса не нужно. */
function BotNumbers({ bot }: { bot: PartnerBot }) {
  const items = [
    { label: 'Подписок', value: bot.activeSubs.toLocaleString('ru-RU') },
    { label: 'Клиентов', value: bot.totalUsers.toLocaleString('ru-RU') },
    { label: 'Доход в месяц', value: `${bot.mrrRub.toLocaleString('ru-RU')} ₽` },
  ];
  return (
    <div className="surface-card grid grid-cols-3 divide-x divide-white/[0.05] rounded-[20px] py-3.5">
      {items.map((it) => (
        <div key={it.label} className="min-w-0 px-3 text-center">
          <div className="nums truncate font-display text-[19px] font-[330]">{it.value}</div>
          <div className="mt-0.5 truncate text-[11.5px] text-dim">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 px-1 text-[12.5px] text-dim">{title}</h2>
      <RowGroup>{children}</RowGroup>
    </section>
  );
}

function BotHubSkeleton() {
  return (
    <SkeletonPage className="p-4 pb-6 space-y-5">
      <div className="flex flex-col items-center pt-3">
        <Skeleton className="size-16 rounded-[18px]" />
        <SkeletonLine size="lg" className="mt-3 w-40" />
        <SkeletonLine size="sm" className="mt-2 w-32" />
      </div>
      <SkeletonCard className="rounded-[20px]" style={{ minHeight: 70 }} />
      {[2, 4, 1, 2].map((n, i) => (
        <section key={i}>
          <SkeletonLine size="sm" className="mb-2 ml-1 w-24" />
          <SkeletonCard className="rounded-[20px]" style={{ minHeight: n * 61 }} />
        </section>
      ))}
    </SkeletonPage>
  );
}
